import {
  fieldProblemReasonLabels,
  fieldProblemReasonSchema,
  type FieldMissionItem,
  type FieldProblemReason,
} from '@groupe-reca/contracts'
import { ArrowLeft, Copy, MessageSquare, Navigation, Phone, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { formatPhone, formatTime, hasCoordinates, itemAddress, navigationUrl, telHref } from '../../domain/fieldText'
import { StreetView } from './StreetView'

// Repris (adapt) de reca-app-v3 `FieldFiche` : seulement ce que l'intervention exige. Aucun prix, paiement ni
// renseignement financier. Les SMS passeront par le service central RECA (consentement du client), pas par un
// bouton de l'opérateur : emplacement réservé tant que ce service n'est pas raccordé.

const eventLabels: Record<string, string> = {
  COMPLETED: 'Travail confirmé',
  PROBLEM_REPORTED: 'Problème signalé',
  PROBLEM_RESOLVED: 'Problème résolu, travail confirmé',
  CLEARED: 'Classée par le bureau',
}

export interface ProblemDraft {
  reasonCode: FieldProblemReason | null
  reasonText: string
  note: string
}

export interface ItemSheetProps {
  item: FieldMissionItem
  backLabel: string
  /** Saisie conservée en cas d'erreur (relevée par le parent). */
  draft: ProblemDraft
  onDraftChange: (draft: ProblemDraft) => void
  problemState: { status: 'idle' | 'saving' | 'error'; message?: string }
  canReport: boolean
  /** Clé Google (vue de rue à la demande) et réseau disponible. */
  mapsApiKey: string | undefined
  online: boolean
  /** Ouvre directement le signalement (bouton « Problème » de la carte Résidence). */
  startWithProblem?: boolean
  onBack: () => void
  onReportProblem: (draft: ProblemDraft) => Promise<boolean>
}

const reasons = fieldProblemReasonSchema.options

export function ItemSheet({
  item,
  backLabel,
  draft,
  onDraftChange,
  problemState,
  canReport,
  mapsApiKey,
  online,
  startWithProblem = false,
  onBack,
  onReportProblem,
}: ItemSheetProps) {
  const [dialogOpen, setDialogOpen] = useState(startWithProblem && canReport && item.status !== 'COMPLETED')
  const [touched, setTouched] = useState(false)
  const [copyNotice, setCopyNotice] = useState<string | null>(null)
  const phoneHref = telHref(item.phone)
  const textRequired = draft.reasonCode === 'OTHER' && draft.reasonText.trim() === ''
  const invalid = draft.reasonCode === null || textRequired
  const instructions = [
    { label: 'Consignes d’accès', value: item.locationInstructions },
    { label: 'Message au déneigeur', value: item.operatorMessage },
    { label: 'Consignes de déneigement', value: item.specialInstructions },
    { label: 'Obstacles connus', value: item.knownObstacles },
  ].filter((entry): entry is { label: string; value: string } => Boolean(entry.value))

  async function copyPhone() {
    if (!item.phone) return
    try {
      await navigator.clipboard.writeText(item.phone)
      setCopyNotice('Numéro copié')
    } catch {
      setCopyNotice(`Copie impossible — numéro : ${formatPhone(item.phone)}`)
    }
  }

  async function submit() {
    setTouched(true)
    if (invalid) return
    if (await onReportProblem(draft)) {
      setDialogOpen(false)
      setTouched(false)
    }
  }

  return (
    <section className="field-fiche" aria-label="Fiche terrain">
      <div className="field-fiche__bar">
        <button type="button" className="field-button field-button--outline" onClick={onBack}>
          <ArrowLeft aria-hidden="true" /> {backLabel}
        </button>
        <span className="field-eyebrow">Résidence n° {item.order}</span>
      </div>
      <div className="field-fiche__body">
        <h2 className="field-fiche__address">{itemAddress(item.snapshot)}</h2>
        <p className="field-fiche__muted">
          {[item.snapshot.city, item.snapshot.postalCode].filter(Boolean).join(' ')}
          {hasCoordinates(item.snapshot) ? '' : ' · sans coordonnées : position non affichée sur la carte'}
        </p>
        {item.snapshot.clientName ? <p className="field-fiche__name">{item.snapshot.clientName}</p> : null}
        {item.phone ? (
          <p className="field-fiche__phone">{formatPhone(item.phone)}</p>
        ) : (
          <p className="field-fiche__muted">Aucun numéro de téléphone au dossier</p>
        )}

        <div className="field-fiche__actions">
          <a
            className="field-button field-button--primary field-button--tall"
            href={navigationUrl(item.snapshot)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Navigation aria-hidden="true" /> Naviguer
          </a>
          {phoneHref ? (
            <>
              <a className="field-button field-button--outline field-button--tall" href={phoneHref}>
                <Phone aria-hidden="true" /> Appeler
              </a>
              <button
                type="button"
                className="field-button field-button--outline field-button--tall"
                onClick={() => void copyPhone()}
              >
                <Copy aria-hidden="true" /> Copier le numéro
              </button>
            </>
          ) : (
            <span className="field-fiche__disabled" aria-disabled="true">
              Appel impossible : numéro absent ou illisible
            </span>
          )}
          <span className="op-sms" aria-disabled="true">
            <MessageSquare aria-hidden="true" /> SMS · service RECA à raccorder
          </span>
          {canReport && item.status !== 'COMPLETED' ? (
            <button
              type="button"
              className="field-button field-button--attempt field-button--tall"
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              <TriangleAlert aria-hidden="true" /> Signaler un problème
            </button>
          ) : null}
        </div>
        <p className="field-fiche__muted">
          L’appel passe par le téléphone de l’opérateur si la tablette n’a pas de ligne voix. Les SMS partent du
          service central RECA, selon le consentement du client.
        </p>
        {copyNotice ? (
          <p className="field-fiche__notice" role="status">
            {copyNotice}
          </p>
        ) : null}

        {instructions.map((entry) => (
          <div key={entry.label} className="field-fiche__block">
            <span className="field-eyebrow">{entry.label}</span>
            <p>{entry.value}</p>
          </div>
        ))}

        <StreetView
          apiKey={mapsApiKey}
          position={
            item.snapshot.latitude !== null && item.snapshot.longitude !== null
              ? { lat: item.snapshot.latitude, lng: item.snapshot.longitude }
              : null
          }
          online={online}
          address={itemAddress(item.snapshot)}
        />

        <div className="field-fiche__block">
          <span className="field-eyebrow">Historique dans cette mission</span>
          {item.events.length === 0 ? (
            <p className="field-fiche__muted">Aucun événement.</p>
          ) : (
            <ol className="op-history">
              {item.events.map((event) => (
                <li key={event.id} className={event.kind === 'PROBLEM_REPORTED' ? 'is-problem' : undefined}>
                  <strong>{eventLabels[event.kind] ?? event.kind}</strong> · {formatTime(event.occurredAt)}
                  {event.actorName ? ` · ${event.actorName}` : ''}
                  {event.reasonCode ? (
                    <div>
                      {fieldProblemReasonLabels[event.reasonCode]}
                      {event.reasonText ? ` — ${event.reasonText}` : ''}
                    </div>
                  ) : null}
                  {event.note ? <div className="field-fiche__muted">{event.note}</div> : null}
                </li>
              ))}
            </ol>
          )}
        </div>
        {item.status !== 'COMPLETED' ? (
          <p className="field-fiche__reminder">
            Pour confirmer le travail, maintenez 3 secondes le bouton vert de la résidence, dans le panneau de gauche.
          </p>
        ) : null}
      </div>

      {dialogOpen ? (
        <div className="field-reason" role="dialog" aria-modal="true" aria-label="Signaler un problème">
          <div className="field-reason__card">
            <h3>Quel est le problème ?</h3>
            <div className="op-reasons" role="radiogroup" aria-label="Motif">
              {reasons.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  role="radio"
                  aria-checked={draft.reasonCode === reason}
                  className={`op-reason${draft.reasonCode === reason ? ' is-selected' : ''}`}
                  onClick={() => {
                    onDraftChange({ ...draft, reasonCode: reason })
                  }}
                >
                  {fieldProblemReasonLabels[reason]}
                </button>
              ))}
            </div>
            {draft.reasonCode === 'OTHER' ? (
              <>
                <label htmlFor="op-reason-text">Explication (obligatoire)</label>
                <textarea
                  id="op-reason-text"
                  value={draft.reasonText}
                  maxLength={500}
                  aria-invalid={touched && textRequired}
                  onChange={(event) => {
                    onDraftChange({ ...draft, reasonText: event.target.value })
                  }}
                />
              </>
            ) : null}
            <label htmlFor="op-reason-note">Note complémentaire (facultative)</label>
            <textarea
              id="op-reason-note"
              value={draft.note}
              maxLength={1000}
              onChange={(event) => {
                onDraftChange({ ...draft, note: event.target.value })
              }}
            />
            {touched && invalid ? (
              <p className="field-reason__error" role="alert">
                {draft.reasonCode === null ? 'Choisissez un motif.' : 'Pour « Autre », expliquez le problème.'}
              </p>
            ) : null}
            {problemState.status === 'error' ? (
              <p className="field-reason__error" role="alert">
                {problemState.message ?? 'Enregistrement impossible. Votre saisie est conservée.'}
              </p>
            ) : null}
            <div className="field-reason__actions">
              <button
                type="button"
                className="field-button field-button--outline"
                disabled={problemState.status === 'saving'}
                onClick={() => {
                  setDialogOpen(false)
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                className="field-button field-button--primary"
                disabled={problemState.status === 'saving'}
                onClick={() => void submit()}
              >
                {problemState.status === 'saving' ? 'Enregistrement…' : 'Signaler le problème'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
